import { useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { ROOM_HEIGHT, ROOM_WIDTH, clampToRoom } from "../editor/geometry";
import type { DraftTable } from "../editor/types";

/**
 * The drag primitive (Tech-decision "Canvas & drag tech"): native Pointer Events with
 * `setPointerCapture`, unifying mouse + touch for tablets with zero dependencies. During the drag the
 * position is transient LOCAL state (a "ghost") so the move stays fluid and pushes NO undo entries;
 * only pointer-up commits ONE snapped `moveTable` (grid snap happens in the reducer). Because the
 * elements are real SVG nodes, the whole gesture is assertable in Playwright and axe — the reason a
 * bitmap canvas lib was rejected.
 */

export interface Ghost {
  id: string;
  x: number;
  y: number;
}

interface DragRef {
  id: string;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  scaleX: number;
  scaleY: number;
  curX: number;
  curY: number;
}

export interface TableDragHandlers {
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: (e: ReactPointerEvent) => void;
  onPointerCancel: (e: ReactPointerEvent) => void;
}

export interface UseTableDragResult {
  ghost: Ghost | null;
  dragHandlers: (table: DraftTable) => TableDragHandlers;
}

export function useTableDrag(opts: {
  disabled?: boolean;
  onCommit: (id: string, x: number, y: number) => void;
  onPick?: (id: string) => void;
}): UseTableDragResult {
  const { disabled, onCommit, onPick } = opts;
  const [ghost, setGhost] = useState<Ghost | null>(null);
  const dragRef = useRef<DragRef | null>(null);

  function begin(e: ReactPointerEvent, table: DraftTable) {
    if (disabled || e.button !== 0) return;
    onPick?.(table.id);
    const surface = (e.currentTarget as Element).closest("[data-plan-surface]");
    const rect = surface?.getBoundingClientRect();
    // Independent x/y scales: the SVG uses preserveAspectRatio="none", so if the surface's rendered
    // ratio ever diverges from ROOM_WIDTH:ROOM_HEIGHT (a max-height clamp, a future zoom), a single
    // width-derived scale would miscalibrate vertical drag. Deriving each axis from its own extent
    // keeps the ghost under the pointer regardless.
    const scaleX = rect && rect.width ? rect.width / ROOM_WIDTH : 1;
    const scaleY = rect && rect.height ? rect.height / ROOM_HEIGHT : 1;
    dragRef.current = {
      id: table.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: table.x,
      startY: table.y,
      scaleX,
      scaleY,
      curX: table.x,
      curY: table.y,
    };
    setGhost({ id: table.id, x: table.x, y: table.y });
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      // jsdom / older engines: capture is best-effort, the window-less move still works.
    }
  }

  function move(e: ReactPointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startClientX) / (d.scaleX || 1);
    const dy = (e.clientY - d.startClientY) / (d.scaleY || 1);
    const p = clampToRoom(d.startX + dx, d.startY + dy);
    d.curX = p.x;
    d.curY = p.y;
    setGhost({ id: d.id, x: p.x, y: p.y });
  }

  function end(e: ReactPointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* best-effort */
    }
    setGhost(null);
    if (d.curX !== d.startX || d.curY !== d.startY) onCommit(d.id, d.curX, d.curY);
  }

  return {
    ghost,
    dragHandlers: (table) => ({
      onPointerDown: (e) => begin(e, table),
      onPointerMove: move,
      onPointerUp: end,
      onPointerCancel: end,
    }),
  };
}
